import React, { useEffect, useMemo, useRef, useState } from 'react';
import appStoreLogo from '../assets/app_store_logo.svg';
import playStoreLogo from '../assets/play_store_logo.svg';
import BookingForm from '../components/BookingForm';
import {
  initMetaPixel,
  trackDemoClick,
  trackLead,
} from '../utils/meta-pixel';

// Visuels et animations issus de we-merchant-mobile/marketing/site/ (mockups + boucles).
const ASSET = `${process.env.PUBLIC_URL || ''}/assets/app-marchand/`;
const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_DEMO_NUMBER || '';
const EXPLAINER_VIDEO_URL = process.env.REACT_APP_MOBILE_EXPLAINER_VIDEO_URL || '';
const EXPLAINER_VIDEO_POSTER =
  process.env.REACT_APP_MOBILE_EXPLAINER_VIDEO_POSTER || `${ASSET}hero.webp`;
const NORMALIZED_EXPLAINER_VIDEO_URL = EXPLAINER_VIDEO_URL.trim();
const HAS_EXPLAINER_VIDEO = NORMALIZED_EXPLAINER_VIDEO_URL.length > 0;
const DEFAULT_WHATSAPP_TEXT =
  'Bonjour, je veux une démo OuiGoo. Je vends en ligne et je veux tester le lien/QR et la livraison avec suivi.';

function pushEvent(event, params = {}) {
  if (typeof window === 'undefined') return;
  if (typeof window.gtag === 'function') window.gtag('event', event, params);
  if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event, ...params });
}

function getUtmParams() {
  if (typeof window === 'undefined') return {};
  const sp = new URLSearchParams(window.location.search);
  return {
    utm_source: sp.get('utm_source') || '',
    utm_medium: sp.get('utm_medium') || '',
    utm_campaign: sp.get('utm_campaign') || '',
    utm_content: sp.get('utm_content') || '',
  };
}

// Détecte les navigateurs intégrés (TikTok, Instagram, Facebook...) où iOS bloque l'ouverture de l'App Store.
function isInAppBrowser() {
  if (typeof navigator === 'undefined') return false;
  const ua = (navigator.userAgent || '').toLowerCase();
  return /musical_ly|bytedance|tiktok|instagram|fbav|fban|fb_iab|line\/|snapchat/.test(ua);
}

function appendUtms(url, utmParams) {
  if (!url) return '';
  try {
    const out = new URL(url, window.location.origin);
    Object.entries(utmParams).forEach(([key, value]) => {
      if (value) out.searchParams.set(key, value);
    });
    return out.toString();
  } catch (_) {
    return url;
  }
}

function buildWhatsappLink(utmParams) {
  const message = encodeURIComponent(DEFAULT_WHATSAPP_TEXT);
  const base = WHATSAPP_NUMBER ? `https://wa.me/${WHATSAPP_NUMBER}?text=${message}` : `https://wa.me/?text=${message}`;
  return appendUtms(base, utmParams);
}

function getStoreRedirectUrl(store, utmParams) {
  const base = `/go/${store}`;
  const params = new URLSearchParams();
  Object.entries(utmParams).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

function toYoutubeEmbedUrl(url) {
  if (!url) return '';
  try {
    const u = new URL(url);
    const host = u.hostname.replace('www.', '');
    if (host === 'youtu.be') {
      const id = u.pathname.replace('/', '').trim();
      return id ? `https://www.youtube.com/embed/${id}` : '';
    }
    if (host === 'youtube.com' || host === 'm.youtube.com') {
      if (u.pathname.startsWith('/watch')) {
        const id = u.searchParams.get('v');
        return id ? `https://www.youtube.com/embed/${id}` : '';
      }
      const parts = u.pathname.split('/').filter(Boolean);
      if (parts[0] === 'shorts' && parts[1]) {
        return `https://www.youtube.com/embed/${parts[1]}`;
      }
      if (parts[0] === 'embed' && parts[1]) return url;
    }
    return '';
  } catch (_) {
    return '';
  }
}

// Chaque onglet affiche une ou deux animations (écrans réels de l'app), en boucle et sans son.
const moduleTabs = [
  {
    id: 'shop_link',
    label: 'Boutique',
    title: 'Votre boutique en ligne et son lien',
    text: 'Créez votre catalogue en quelques minutes : photos, prix, variantes. Partagez le lien de votre boutique sur WhatsApp, Instagram, Facebook, TikTok et dans vos publicités.',
    loops: ['boutique', 'partage'],
  },
  {
    id: 'order_link_qr',
    label: 'Commande',
    title: 'Le client commande et partage sa position',
    text: 'Le client choisit son produit, le paiement à la livraison est déjà activé, et il indique sa position sur la carte : pas besoin d’adresse.',
    loops: ['commande'],
  },
  {
    id: 'shipping',
    label: 'Expédition',
    title: 'Expédition en 1 clic et tournées optimisées',
    text: 'Confiez la commande à vos livreurs, au Réseau OuiGoo ou à une société partenaire. Regroupez plusieurs commandes : OuiGoo organise la tournée et indique le montant à collecter à chaque arrêt.',
    loops: ['expedition', 'tournees'],
  },
  {
    id: 'tracking_notifications',
    label: 'Suivi & Notifications',
    title: 'Suivi en direct et notifications WhatsApp',
    text: 'Suivez le livreur sur la carte jusqu’à la porte du client. Votre client est prévenu sur WhatsApp : commande confirmée, livreur en route.',
    loops: ['suivi', 'whatsapp'],
  },
  {
    id: 'cod_balance',
    label: 'Encaissement',
    title: 'Paiement à la livraison et reversement',
    text: 'Le livreur collecte le paiement. Chaque montant remonte dans votre solde, puis vous est reversé, avec le détail par commande.',
    loops: ['solde'],
  },
  {
    id: 'ads_integrations',
    label: 'Pixel & Intégrations',
    title: 'Pixel Meta, Shopify et WooCommerce',
    text: 'Ajoutez votre pixel Meta pour mesurer vos publicités. Vous vendez déjà sur Shopify ou WooCommerce ? Connectez votre boutique et livrez vos commandes avec OuiGoo.',
    loops: ['pixel', 'shopify'],
  },
];

// Vrais marchands OuiGoo affichés dans le bandeau défilant (uniquement avec leur accord).
// Ajouter un marchand = ajouter son nom.
const CLIENTS = ['CHIVI', 'FAFA Styles', 'Don&co Accessories', 'Horizon'];

// Visuels du catalogue dans l'app pour différentes niches (boutiques d'exemple).
const NICHES = [
  { id: 'mode', label: 'Mode' },
  { id: 'cuisine', label: 'Cuisine' },
  { id: 'beaute', label: 'Beauté' },
  { id: 'electronique', label: 'Électronique' },
  { id: 'epicerie', label: 'Épicerie' },
];

const LOOP_LABELS = {
  boutique: 'Création de la boutique',
  partage: 'Partage du lien de la boutique',
  commande: 'Commande du client avec sa position',
  expedition: 'Expédition d’une commande',
  tournees: 'Tournée optimisée',
  suivi: 'Suivi du livreur en direct',
  whatsapp: 'Notification WhatsApp au client',
  solde: 'Solde des paiements collectés',
  pixel: 'Ajout du pixel Meta',
  shopify: 'Synchronisation Shopify',
};

// Animation muette en boucle ; si l'utilisateur limite les animations, seul le poster est affiché.
const prefersReducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function LoopVideo({ name, className }) {
  const ref = useRef(null);
  const animate = useMemo(() => !prefersReducedMotion(), []);
  useEffect(() => {
    const v = ref.current;
    if (!v || !animate) return;
    v.muted = true;
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  }, [name, animate]);
  return (
    <div className={`overflow-hidden rounded-3xl bg-white shadow-xl ${className || ''}`}>
      <video
        ref={ref}
        key={name}
        className="block aspect-square h-auto w-full object-cover"
        poster={`${ASSET}p_${name}.jpg`}
        autoPlay={animate}
        muted
        loop
        playsInline
        preload="metadata"
        aria-label={`${LOOP_LABELS[name] || name} dans l’app OuiGoo`}
      >
        <source src={`${ASSET}v_${name}.webm`} type="video/webm" />
        <source src={`${ASSET}v_${name}.mp4`} type="video/mp4" />
      </video>
    </div>
  );
}

export default function MobileAppLanding() {
  const [fired50, setFired50] = useState(false);
  const [fired90, setFired90] = useState(false);
  const [activeTab, setActiveTab] = useState(moduleTabs[0].id);
  const howItWorksRef = useRef(null);
  const pricingRef = useRef(null);
  const videoMilestonesRef = useRef({ 25: false, 50: false, 75: false, 100: false });
  const utmParams = useMemo(() => getUtmParams(), []);
  const inAppBrowser = useMemo(() => isInAppBrowser(), []);
  const whatsappLink = useMemo(() => buildWhatsappLink(utmParams), [utmParams]);
  const playStoreRedirectUrl = useMemo(() => getStoreRedirectUrl('play-store', utmParams), [utmParams]);
  const appStoreRedirectUrl = useMemo(() => getStoreRedirectUrl('app-store', utmParams), [utmParams]);
  const currentTab = moduleTabs.find(tab => tab.id === activeTab) || moduleTabs[0];
  const tabLoops = currentTab.loops || [];
  const youtubeEmbedUrl = useMemo(() => toYoutubeEmbedUrl(NORMALIZED_EXPLAINER_VIDEO_URL), []);
  const [youtubeTracked, setYoutubeTracked] = useState(false);
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  // Navigateur intégré (Facebook, Instagram, TikTok…) : l'App Store peut y être bloqué.
  // On n'affiche l'aide qu'au moment où la personne veut télécharger l'app.
  const [browserHelp, setBrowserHelp] = useState(null); // { url } ou null
  const handleStoreClick = (target, url) => e => {
    pushEvent('cta_store_click', { source: 'badge_strip', target });
    if (inAppBrowser) {
      e.preventDefault();
      setBrowserHelp({ url });
      pushEvent('inapp_browser_help', { target });
    }
  };
  const handleScrollToStoreBadges = source => e => {
    e.preventDefault();
    pushEvent('cta_store_click', { source });
    const target = document.getElementById('store-badges');
    if (!target) return;
    const navHeight = document.querySelector('nav')?.offsetHeight || 0;
    const targetTop = target.getBoundingClientRect().top + window.scrollY;
    const offset = navHeight + 12;
    window.scrollTo({ top: Math.max(targetTop - offset, 0), behavior: 'smooth' });
  };

  useEffect(() => {
    initMetaPixel();
    const previousTitle = document.title;
    document.title = 'OuiGoo | Vendez en ligne comme vos clients achètent';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - doc.clientHeight;
      if (scrollable <= 0) return;
      const ratio = window.scrollY / scrollable;
      if (!fired50 && ratio >= 0.5) {
        pushEvent('scroll_50');
        setFired50(true);
      }
      if (!fired90 && ratio >= 0.9) {
        pushEvent('scroll_90');
        setFired90(true);
      }
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [fired50, fired90]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          if (entry.target === howItWorksRef.current) pushEvent('view_how_it_works');
          if (entry.target === pricingRef.current) pushEvent('view_pricing');
        });
      },
      { threshold: 0.35 }
    );
    if (howItWorksRef.current) observer.observe(howItWorksRef.current);
    if (pricingRef.current) observer.observe(pricingRef.current);
    return () => observer.disconnect();
  }, []);

  const handleVideoPlay = () => {
    pushEvent('video_play', { source: 'explainer' });
  };

  const handleVideoTimeUpdate = e => {
    const video = e.currentTarget;
    if (!video.duration || !Number.isFinite(video.duration)) return;
    const progress = (video.currentTime / video.duration) * 100;
    [25, 50, 75].forEach(step => {
      if (progress >= step && !videoMilestonesRef.current[step]) {
        videoMilestonesRef.current[step] = true;
        pushEvent(`video_${step}`, { source: 'explainer' });
      }
    });
  };

  const handleVideoEnded = () => {
    if (videoMilestonesRef.current[100]) return;
    videoMilestonesRef.current[100] = true;
    pushEvent('video_complete', { source: 'explainer' });
  };

  // « Parler à un conseiller » : ouvre la prise de rendez-vous (API booking, slug demo).
  const openDemoModal = source => {
    setDemoModalOpen(true);
    pushEvent('cta_booking_click', { source });
    trackDemoClick();
  };

  const closeDemoModal = () => setDemoModalOpen(false);

  const handleBooked = () => {
    pushEvent('demo_request_submit', { status: 'success', channel: 'booking' });
    trackLead({
      source: utmParams.utm_source || '',
      campaign: utmParams.utm_campaign || '',
      content: utmParams.utm_content || '',
    });
  };

  // Lien direct vers la prise de rendez-vous : /app#rendez-vous
  useEffect(() => {
    if (window.location.hash === '#rendez-vous') setDemoModalOpen(true);
  }, []);

  useEffect(() => {
    if (!demoModalOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = e => {
      if (e.key === 'Escape') setDemoModalOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [demoModalOpen]);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800 font-inter">
      <main>
        <section className="bg-[#f3ece4] py-16 lg:py-24">
          <div className="mx-auto max-w-screen-2xl px-5 md:px-16">
            <div className="grid items-center gap-10 lg:min-h-[760px] lg:grid-cols-[0.8fr_1.2fr]">
              <div>
                <h1 className="text-4xl lg:text-6xl font-extrabold leading-tight text-gray-900">
                  Vendez en ligne <span className="text-primary-custom">comme vos clients achètent.</span>
                </h1>
                <p className="mt-4 max-w-xl text-lg text-gray-600">
                  Boutique en ligne, paiement à la livraison et livreurs, dans une seule app. Gratuite : 0 FCFA par mois.
                </p>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={() => openDemoModal('hero')}
                    className="inline-flex items-center justify-center rounded-lg bg-primary-custom px-7 py-3 text-base font-semibold text-white transition hover:bg-primary-custom-dark"
                  >
                    Parler à un conseiller
                  </button>
                  <a
                    href="#store-badges"
                    onClick={handleScrollToStoreBadges('hero')}
                    className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-7 py-3 text-base font-semibold text-gray-800 transition hover:bg-gray-100"
                  >
                    Télécharger l'application
                  </a>
                </div>
              </div>

              <div className="flex items-end justify-center lg:justify-end">
                <img
                  src={`${ASSET}hero.webp`}
                  alt="L’app marchand OuiGoo et une publicité sponsorisée menant à la boutique"
                  width="1600"
                  height="1100"
                  className="w-full max-w-[880px] h-auto bg-transparent object-contain"
                />
              </div>
            </div>
          </div>
        </section>

       

        <section id="store-badges" className="bg-[#f8f8f8] py-10 lg:py-14">
          <div className="mx-auto max-w-[1800px] px-5 md:px-12 xl:px-20">
            <div className="mb-8 flex items-center justify-center gap-4 lg:mb-12">
              <a
                href={appStoreRedirectUrl}
                onClick={handleStoreClick('ios', appStoreRedirectUrl)}
                className="inline-block"
              >
                <img src={appStoreLogo} alt="App Store" className="h-11 lg:h-12 xl:h-14" />
              </a>
              <a
                href={playStoreRedirectUrl}
                onClick={handleStoreClick('android', playStoreRedirectUrl)}
                className="inline-block"
              >
                <img src={playStoreLogo} alt="Google Play" className="h-11 lg:h-12 xl:h-14" />
              </a>
            </div>

            <div className="grid gap-4 sm:grid-cols-3 lg:gap-7">
              {[
                {
                  img: '13.webp',
                  alt: 'Vitrine d’une boutique OuiGoo sur téléphone',
                  title: 'Vendez en ligne',
                  text: 'Une boutique gratuite et son lien à partager sur WhatsApp, Instagram, Facebook, TikTok et dans vos publicités.',
                },
                {
                  img: '14.webp',
                  alt: 'Préparation d’une expédition dans l’app OuiGoo',
                  title: 'Livrez partout',
                  text: 'Le client partage sa position sur la carte. Expédiez en 1 clic avec vos livreurs ou le Réseau OuiGoo, avec suivi en direct.',
                },
                {
                  img: '15.webp',
                  alt: 'Détail d’un paiement collecté à la livraison',
                  title: 'Encaissez à la livraison',
                  text: 'Le paiement à la livraison est activé par défaut. Le livreur collecte, OuiGoo vous reverse, avec le détail par commande.',
                },
              ].map(card => (
                <article
                  key={card.title}
                  className="overflow-hidden rounded-2xl border border-gray-200 bg-white lg:rounded-3xl"
                >
                  <img
                    src={`${ASSET}${card.img}`}
                    alt={card.alt}
                    width="900"
                    height="750"
                    loading="lazy"
                    className="block aspect-[6/5] h-auto w-full object-cover"
                  />
                  <div className="p-6 lg:p-8">
                    <p className="text-lg font-bold leading-tight text-gray-900 lg:text-[30px] lg:leading-[1.12]">
                      {card.title}
                    </p>
                    <p className="mt-3 text-sm text-gray-500 lg:text-base">{card.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#113039] py-14 text-white lg:py-20">
          <div className="mx-auto grid max-w-screen-2xl items-center gap-10 px-5 md:px-16 lg:grid-cols-2">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-primary-custom">E-commerçants</p>
              <h2 className="mt-2 text-3xl font-bold leading-tight lg:text-5xl">
                Vous vendez sur WhatsApp, Instagram ou TikTok ?
              </h2>
              <p className="mt-4 text-white/80 lg:text-lg">
                Fini les dizaines de messages pour une commande, les adresses introuvables et les livreurs à appeler un
                par un. OuiGoo transforme vos abonnés en commandes livrées.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {['WhatsApp', 'Facebook', 'Instagram', 'TikTok'].map(channel => (
                  <span key={channel} className="rounded-full border border-white/25 px-3 py-1 text-sm">
                    {channel}
                  </span>
                ))}
              </div>
              <ul className="mt-6 space-y-3">
                {[
                  ['Un lien boutique dans votre bio', 'Le client voit vos produits et vos prix, et commande seul.'],
                  ['Sa position sur la carte', 'Plus besoin d’adresse : le livreur va droit chez le client.'],
                  ['Paiement à la livraison sans risque', 'Le livreur encaisse, OuiGoo vous reverse.'],
                  ['Des pubs qui se mesurent', 'Pixel Meta sur votre boutique pour savoir quelle pub vend.'],
                ].map(([title, text]) => (
                  <li key={title} className="flex gap-3">
                    <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary-custom text-sm font-bold text-white" aria-hidden="true">
                      ✓
                    </span>
                    <span>
                      <span className="font-semibold">{title}</span>
                      <span className="block text-sm text-white/70">{text}</span>
                    </span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => openDemoModal('ecommercants')}
                  className="inline-flex items-center justify-center rounded-lg bg-primary-custom px-6 py-3 font-semibold text-white transition hover:bg-primary-custom-dark"
                >
                  Parler à un conseiller
                </button>
                <a
                  href="#store-badges"
                  onClick={handleScrollToStoreBadges('ecommercants')}
                  className="inline-flex items-center justify-center rounded-lg border border-white/40 px-6 py-3 font-semibold text-white transition hover:bg-white/10"
                >
                  Télécharger l'application
                </a>
              </div>
            </div>
            <div className="flex justify-center">
              <img
                src={`${ASSET}ecommercants.webp`}
                alt="Profil d’une boutique sur les réseaux sociaux avec son lien OuiGoo en bio, et son catalogue dans l’app"
                width="1000"
                height="921"
                loading="lazy"
                className="h-auto w-full max-w-[620px]"
              />
            </div>
          </div>
        </section>

        {CLIENTS.length ? (
          <section className="bg-white py-12 lg:py-16">
            <style>{`
              @keyframes ouigoo-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
              .ouigoo-marquee-track { animation: ouigoo-marquee 30s linear infinite; }
              .ouigoo-marquee:hover .ouigoo-marquee-track { animation-play-state: paused; }
              @media (prefers-reduced-motion: reduce) { .ouigoo-marquee-track { animation: none; } }
            `}</style>
            <div className="mx-auto max-w-screen-2xl px-5 md:px-16">
              <h2 className="text-center text-2xl font-bold text-gray-900 lg:text-4xl">Ils vendent déjà avec OuiGoo</h2>
            </div>
            <div
              className="ouigoo-marquee mt-8 overflow-hidden"
              style={{
                WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 10%, #000 90%, transparent)',
                maskImage: 'linear-gradient(90deg, transparent, #000 10%, #000 90%, transparent)',
              }}
            >
              {/* Deux moitiés identiques : la piste défile de -50 % pour une boucle sans saut. */}
              <ul className="ouigoo-marquee-track flex w-max items-center">
                {[0, 1].map(half =>
                  Array.from({ length: Math.max(2, Math.ceil(8 / CLIENTS.length)) })
                    .flatMap(() => CLIENTS)
                    .map((name, i) => (
                      <li
                        key={`${half}-${i}`}
                        aria-hidden={half === 1 || i >= CLIENTS.length ? true : undefined}
                        className="flex items-center whitespace-nowrap px-6 text-2xl font-extrabold uppercase tracking-wide text-gray-800 lg:px-10 lg:text-4xl"
                      >
                        {name}
                        <span className="ml-12 inline-block h-2.5 w-2.5 rounded-full bg-primary-custom lg:ml-20" aria-hidden="true" />
                      </li>
                    ))
                )}
              </ul>
            </div>
          </section>
        ) : null}

        <section ref={howItWorksRef} className="bg-[#f2f2f2] py-14 lg:py-18">
          <div className="mx-auto max-w-screen-2xl px-5 md:px-16">
            <div className="mb-7 flex flex-wrap gap-x-5 gap-y-2 text-2xl font-extrabold sm:text-3xl lg:text-5xl" role="tablist">
              {moduleTabs.map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={tab.id === activeTab}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${tab.id === activeTab ? 'text-primary-custom' : 'text-gray-300'} transition`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="grid items-center gap-10 lg:min-h-[700px] lg:grid-cols-[0.8fr_1.2fr]">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 lg:text-5xl">{currentTab.title}</h2>
                <p className="mt-4 text-gray-600 lg:text-lg">{currentTab.text}</p>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={() => openDemoModal(`tab_${currentTab.id}`)}
                    className="inline-flex items-center justify-center rounded-lg bg-primary-custom px-5 py-3 text-sm font-semibold text-white transition hover:bg-primary-custom-dark"
                  >
                    Parler à un conseiller
                  </button>
                  <a
                    href="#store-badges"
                    onClick={handleScrollToStoreBadges(`tab_${currentTab.id}`)}
                    className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-5 py-3 text-sm font-semibold text-gray-800 transition hover:bg-gray-100"
                  >
                    Télécharger l'application
                  </a>
                </div>
              </div>

              <div>
                <div
                  className={`${
                    tabLoops.length > 1 ? 'flex items-center justify-center gap-3 sm:gap-6 lg:justify-end' : 'flex justify-center lg:justify-end'
                  }`}
                >
                  {tabLoops.map(name => (
                    <LoopVideo
                      key={`${currentTab.id}-${name}`}
                      name={name}
                      className={tabLoops.length > 1 ? 'w-1/2 max-w-[420px]' : 'w-full max-w-[560px]'}
                    />
                  ))}
                </div>
                <p className="mt-4 text-center text-sm text-gray-500 lg:text-right">
                  Écrans réels de l’app OuiGoo et de la boutique client
                </p>
              </div>
            </div>
          </div>
        </section>

         {HAS_EXPLAINER_VIDEO ? (
          <section className="bg-[#f2f2f2] py-10 lg:py-14">
            <div className="mx-auto max-w-screen-2xl px-5 md:px-16">
              <div className="mx-auto max-w-5xl">
                <div className="mb-6 text-center">
                  {/* <h2 className="text-3xl lg:text-5xl font-bold text-gray-900">Decouvrir OuiGoo</h2> */}
                  <p className="mt-3 text-gray-600 lg:text-lg">
                    Une vidéo de présentation pour comprendre OuiGoo et comment l’app simplifie la vente en ligne et la livraison.
                  </p>
                </div>

                <div className="mx-auto overflow-hidden rounded-2xl border border-gray-200 bg-black">
                  <div className="aspect-video">
                    {youtubeEmbedUrl ? (
                      <div
                        className="h-full w-full"
                        onPointerDown={() => {
                          if (youtubeTracked) return;
                          setYoutubeTracked(true);
                          pushEvent('video_play', { source: 'explainer_youtube' });
                        }}
                      >
                        <iframe
                          title="Ouigoo video explicative"
                          src={youtubeEmbedUrl}
                          className="h-full w-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          referrerPolicy="strict-origin-when-cross-origin"
                          allowFullScreen
                        />
                      </div>
                    ) : (
                      <video
                        className="h-full w-full"
                        controls
                        preload="metadata"
                        poster={EXPLAINER_VIDEO_POSTER}
                        onPlay={handleVideoPlay}
                        onTimeUpdate={handleVideoTimeUpdate}
                        onEnded={handleVideoEnded}
                      >
                      <source src={NORMALIZED_EXPLAINER_VIDEO_URL} type="video/mp4" />
                        Votre navigateur ne supporte pas la vidéo HTML5.
                      </video>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        ) : null}

        <section className="bg-white py-14 lg:py-20">
          <div className="mx-auto max-w-screen-2xl px-5 md:px-16">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-3xl font-bold text-gray-900 lg:text-5xl">Une boutique pour chaque activité</h2>
              <p className="mt-3 text-gray-600 lg:text-lg">
                Mode, cuisine, beauté, électronique ou épicerie : créez votre catalogue en quelques minutes dans l’app,
                avec le paiement à la livraison déjà activé.
              </p>
            </div>

            {/* Mobile : un téléphone qui enchaîne les niches */}
            <div className="mt-8 flex justify-center lg:hidden">
              <img
                src={`${ASSET}niches/catalogues.webp`}
                alt="Catalogues de boutiques mode, cuisine, beauté, électronique et épicerie dans l’app OuiGoo"
                width="600"
                height="1236"
                loading="lazy"
                className="h-auto w-full max-w-[300px]"
              />
            </div>

            {/* Desktop : les cinq niches côte à côte */}
            <div className="mt-12 hidden gap-6 lg:grid lg:grid-cols-5">
              {NICHES.map(niche => (
                <figure key={niche.id} className="text-center">
                  <img
                    src={`${ASSET}niches/${niche.id}.webp`}
                    alt={`Catalogue d’une boutique ${niche.label.toLowerCase()} dans l’app OuiGoo`}
                    width="520"
                    height="1071"
                    loading="lazy"
                    className="mx-auto h-auto w-full max-w-[260px]"
                  />
                  <figcaption className="mt-3 font-semibold text-gray-900">{niche.label}</figcaption>
                </figure>
              ))}
            </div>
            <p className="mt-6 text-center text-xs text-gray-400">Boutiques d’exemple, à titre d’illustration.</p>
          </div>
        </section>

        <section className="bg-[#1f2127] py-16 text-white">
          <div className="mx-auto max-w-screen-2xl px-5 md:px-16">
            <div className="grid gap-10 lg:min-h-[820px] lg:grid-cols-[0.7fr_1.3fr] lg:items-center">
              <div>
                <h2 className="text-3xl font-bold leading-tight lg:text-4xl">
                  Sur mobile et sur ordinateur
                </h2>

                <div className="mt-8 space-y-8 text-gray-300">
                  <div>
                    <p className="text-lg font-semibold text-white">L’espace web business.ouigoo.co</p>
                    <p className="mt-2 text-sm">
                      Retrouvez vos commandes, expéditions et encaissements sur ordinateur, en plus de l’app.
                    </p>
                  </div>

                  <div>
                    <p className="text-lg font-semibold text-white">Shopify, WooCommerce, PrestaShop et API</p>
                    <p className="mt-2 text-sm">
                      Déjà une boutique en ligne ? Connectez-la depuis l’app et synchronisez vos commandes pour les livrer avec OuiGoo.
                      Sur Shopify, installez directement l’application OuiGoo.
                    </p>
                    <a
                      href="https://apps.shopify.com/ouigoo"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => pushEvent('cta_shopify_app_click', { source: 'integrations' })}
                      className="mt-3 inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-gray-900 transition hover:bg-gray-100"
                    >
                      <span className="inline-block h-2 w-2 rounded-full bg-[#95BF47]" aria-hidden="true" />
                      Voir OuiGoo sur le Shopify App Store
                    </a>
                  </div>

                  <div>
                    <p className="text-lg font-semibold text-white">0 FCFA par mois</p>
                    <p className="mt-2 text-sm">
                      Votre boutique est gratuite. Vous payez uniquement les livraisons du Réseau OuiGoo, quand vous l’utilisez.
                    </p>
                  </div>
                </div>
              </div>

              <div className="relative flex justify-center pb-10 lg:justify-end lg:pb-0">
                <img
                  src={`${ASSET}laptop.webp`}
                  alt="Commandes d’une boutique en ligne synchronisées dans l’espace OuiGoo"
                  loading="lazy"
                  className="w-full max-w-[900px] xl:max-w-[1120px] h-auto bg-transparent object-contain"
                />
                <img
                  src={`${ASSET}integrations.webp`}
                  alt="Écran Intégrations de l’app OuiGoo : Shopify, WooCommerce, PrestaShop et API"
                  width="600"
                  height="1236"
                  loading="lazy"
                  className="absolute bottom-0 left-0 h-auto w-[40%] max-w-[260px] drop-shadow-2xl sm:w-[34%] lg:-bottom-10 lg:left-2"
                />
              </div>
            </div>

           
            
          </div>
        </section>

        <section className="bg-white py-14 lg:py-16">
          <div ref={pricingRef} className="mx-auto max-w-screen-2xl px-5 md:px-16">
            <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
              <div>
                <h3 className="text-3xl lg:text-5xl font-bold text-gray-900">FAQ</h3>
                <p className="mt-3 text-gray-600 lg:text-lg">
                  Tarifs, paiement à la livraison, position du client, livreurs et intégrations.
                </p>
                <button
                  type="button"
                  onClick={() => openDemoModal('faq')}
                  className="mt-5 inline-flex text-sm font-semibold text-primary-custom hover:underline"
                >
                  Parler à un conseiller
                </button>
              </div>

              <div className="space-y-3">
                {[
                  ['Combien coûte OuiGoo ?', 'Votre boutique est gratuite : 0 FCFA d’abonnement. Vous payez uniquement les livraisons du Réseau OuiGoo, quand vous choisissez de l’utiliser.'],
                  ['Le paiement à la livraison est-il inclus ?', 'Oui. Il est activé par défaut : le livreur collecte le paiement, le montant remonte dans votre solde et OuiGoo vous le reverse.'],
                  ['Mon client doit-il donner une adresse ?', 'Non. Il indique simplement sa position sur la carte. Pour une commande prise par WhatsApp, OuiGoo peut lui envoyer un lien pour partager sa position.'],
                  ['Puis-je livrer avec mes propres livreurs ?', 'Oui : vos livreurs, le Réseau OuiGoo ou une société de livraison partenaire, selon la commande.'],
                  ['Puis-je regrouper plusieurs commandes ?', 'Oui. OuiGoo organise des tournées optimisées et indique au livreur le montant à collecter à chaque arrêt.'],
                  ['Mon client est-il prévenu ?', 'Oui. Il reçoit des notifications WhatsApp (commande confirmée, livreur en route) et peut suivre la livraison en direct.'],
                  ['Je vends déjà sur Shopify ou WooCommerce, est-ce compatible ?', 'Oui. Sur Shopify, installez l’application OuiGoo depuis le Shopify App Store (apps.shopify.com/ouigoo). Pour WooCommerce et PrestaShop, connectez votre boutique depuis business.ouigoo.co. Vos commandes se synchronisent et vous les livrez avec OuiGoo.'],
                  ['Comment se passe le rendez-vous avec un conseiller ?', 'Vous choisissez un créneau, un conseiller OuiGoo vous contacte par téléphone ou WhatsApp pour comprendre votre activité et vous accompagner : création de votre boutique en ligne, mise en place des livraisons, ou les deux. C’est gratuit.'],
                ].map(([q, a]) => (
                  <details key={q} className="rounded-xl border border-gray-200 bg-gray-50 p-5">
                    <summary className="cursor-pointer font-semibold text-gray-900">{q}</summary>
                    <p className="mt-2 text-sm text-gray-600">{a}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      {browserHelp ? (
        <div
          className="fixed inset-0 z-[95] flex items-end justify-center bg-black/50 sm:items-center sm:px-4"
          onClick={() => setBrowserHelp(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="browser-help-title"
            className="w-full max-w-md rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-2xl"
            onClick={e => e.stopPropagation()}
          >
            <h3 id="browser-help-title" className="text-xl font-bold text-gray-900">
              Ouvrez la page dans votre navigateur
            </h3>
            <p className="mt-2 text-sm text-gray-600">
              Pour installer l’app, appuyez sur ⋯ en haut à droite, puis sur « Ouvrir dans le navigateur ».
            </p>
            <div className="mt-5 flex flex-col gap-3">
              <a
                href={browserHelp.url}
                className="inline-flex h-12 items-center justify-center rounded-xl border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-800 hover:bg-gray-100"
              >
                Essayer quand même
              </a>
              <button
                type="button"
                onClick={() => { setBrowserHelp(null); openDemoModal('inapp_help'); }}
                className="inline-flex h-12 items-center justify-center rounded-xl bg-primary-custom px-4 text-sm font-semibold text-white"
              >
                Parler à un conseiller
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {demoModalOpen ? (
        <div
          className="fixed inset-0 z-[90] flex items-end justify-center bg-black/50 sm:items-center sm:px-4"
          onClick={closeDemoModal}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="booking-title"
            className="max-h-[92vh] w-full max-w-xl overflow-y-auto rounded-t-3xl bg-white p-5 shadow-2xl sm:max-h-[90vh] sm:max-w-2xl sm:rounded-2xl sm:p-8 lg:max-w-3xl lg:p-10"
            onClick={e => e.stopPropagation()}
          >
            <div className="mb-5 flex items-start justify-between gap-3">
              <div>
                <h3 id="booking-title" className="text-2xl font-bold text-gray-900">
                  Prendre rendez-vous avec un conseiller
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  Gratuit. Un conseiller OuiGoo vous appelle au créneau choisi : boutique en ligne, livraisons ou les deux, il
                  répond à vos besoins.
                </p>
              </div>
              <button
                type="button"
                onClick={closeDemoModal}
                aria-label="Fermer"
                className="flex h-10 w-10 flex-none items-center justify-center rounded-full text-2xl leading-none text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              >
                ×
              </button>
            </div>

            <BookingForm slug="demo" source="vitrine-app" whatsappLink={whatsappLink} onBooked={handleBooked} />
          </div>
        </div>
      ) : null}
    </div>
  );
}

